import * as React from 'react';

import { compose } from 'recompose';

import mapProps from 'js/lib/mapProps';

import { AuditUpgradeFlowDataContext } from 'bundles/audit/contexts/AuditUpgradeFlowDataContext';
import { isItemLockedToAuditUpgradeFlowUsers } from 'bundles/audit/utils/isLocked';
import { Tooltip } from 'bundles/common/components/Tooltip';
import type { Placement } from 'bundles/common/components/Tooltip';
import withIsScheduleIncentivesEnabled from 'bundles/course-home/page-course-week/utils/withIsScheduleIncentivesEnabled';
import withSessionLabel from 'bundles/course-sessions/utils/withSessionLabel';
import type { SessionLabel } from 'bundles/course-sessions/utils/withSessionLabel';
import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getFormattedLockMessage } from 'bundles/learner-progress/utils/Item';
import { shouldShowExperimentalUxCopyChangeLockMessage } from 'bundles/learner-progress/utils/shouldShowExperimentalUxCopyChangeLockMessage';

import _t from 'i18n!nls/learner-progress';

type InputProps = {
  computedItem: Item;
  placement: Placement;
  showDistinctAvailabilityReviewDates?: boolean;
};

type Props = InputProps & {
  sessionsV2EnrollmentEnabled: boolean;
  sessionLabel: SessionLabel;
  isScheduleIncentivesEnabled: boolean;
};

class LockedTooltip extends React.Component<Props> {
  static defaultProps: Partial<InputProps> = {
    placement: 'right',
  };

  render() {
    const {
      sessionLabel,
      computedItem: item,
      placement,
      sessionsV2EnrollmentEnabled,
      isScheduleIncentivesEnabled,
      showDistinctAvailabilityReviewDates,
    } = this.props;
    if (!item.itemLockedStatus) {
      return null;
    }

    const { itemLockedStatus, itemLockedReasonCode, itemLockSummary, deadline } = item;

    const formattedLockMessageOptions = {
      sessionsV2Enabled: sessionsV2EnrollmentEnabled,
      sessionLabel,
      itemLockedStatus,
      itemLockedReasonCode,
      itemLockSummary,
      isScheduleIncentivesEnabled,
      deadline,
      showDistinctAvailabilityReviewDates,
    };

    const showExperimentalUxCopyChangeLockMessage = shouldShowExperimentalUxCopyChangeLockMessage({
      ...formattedLockMessageOptions,
    });

    const message = showExperimentalUxCopyChangeLockMessage
      ? _t('To unlock this graded item, extend your target dates.')
      : getFormattedLockMessage(formattedLockMessageOptions);

    // Conditionally setting the margin-top style based on the message and the screen size, otherwise the longer messages will start
    // interfering with other items in the navbar
    const tooltipStyle =
      message !== 'This item is locked.' &&
      item.itemLockedReasonCode !== 'PASSABLE_ITEM_COMPLETION' &&
      window.innerWidth >= 1024
        ? { marginTop: '-68px' }
        : {};

    return (
      <Tooltip
        className="locked-tooltip"
        style={tooltipStyle}
        arrowClassName="locked-tooltip-arrow"
        placement={placement}
      >
        <AuditUpgradeFlowDataContext.Consumer>
          {(context) => {
            return context && isItemLockedToAuditUpgradeFlowUsers(item) ? _t('Upgrade to unlock') : message;
          }}
        </AuditUpgradeFlowDataContext.Consumer>
      </Tooltip>
    );
  }
}

export default compose<Props, InputProps>(
  withSessionsV2EnrollmentEnabled(({ computedItem }) => computedItem?.courseId),
  mapProps(({ computedItem: { courseId } }: Props) => ({
    courseId,
  })),
  withSessionLabel,
  withIsScheduleIncentivesEnabled
)(LockedTooltip);
